import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import {
  aggregateDealerSessionBalances,
  getDealerSessionEffectiveTotal
} from './dealerSessionBalanceUtils';
import { getDealerSessionSortTime, sortDealerSessionsByDate } from './sessionListSortUtils';

export const getDealerSessionDateObject = (session) => {
  const dateStr = session?.orderDate || session?.sessionDate || null;
  if (dateStr) return new Date(`${dateStr}T00:00:00`);
  if (session?.createdAt?.toDate) return session.createdAt.toDate();
  if (session?.createdAt) return new Date(session.createdAt);
  return new Date();
};

export const formatDealerSessionDateLabel = (session) =>
  getDealerSessionDateObject(session).toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

export const formatDealerSessionDateShort = (session) =>
  getDealerSessionDateObject(session).toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });

export const filterDealerSessionsByDateRange = (sessions = [], fromDate, toDate) => {
  if (!fromDate && !toDate) return sessions;
  const fromTs = fromDate ? new Date(`${fromDate}T00:00:00`).getTime() : -Infinity;
  const toTs = toDate ? new Date(`${toDate}T23:59:59.999`).getTime() : Infinity;
  return sessions.filter((session) => {
    const t = getDealerSessionSortTime(session);
    return t >= fromTs && t <= toTs;
  });
};

export const filterDirectPaymentsByDateRange = (payments = [], fromDate, toDate, mode = 'range') => {
  if (mode === 'all' || (!fromDate && !toDate)) return payments;
  const fromTs = fromDate ? new Date(`${fromDate}T00:00:00`).getTime() : -Infinity;
  const toTs = toDate ? new Date(`${toDate}T23:59:59.999`).getTime() : Infinity;
  return (Array.isArray(payments) ? payments : []).filter((payment) => {
    let t = Date.now();
    if (payment?.date) {
      t = new Date(`${payment.date}T00:00:00`).getTime();
    } else if (payment?.createdAt) {
      t = new Date(payment.createdAt).getTime();
    }
    return t >= fromTs && t <= toTs;
  });
};

export const fetchOrdersForDealerSessions = async (sessionIds = []) => {
  if (!sessionIds.length) return [];
  const results = await Promise.all(
    sessionIds.map(async (sessionId) => {
      const ordersQuery = query(
        collection(db, 'orders'),
        where('customerId', '==', sessionId)
      );
      const snapshot = await getDocs(ordersQuery);
      return snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        sessionId,
        ...docSnap.data()
      }));
    })
  );
  return results.flat();
};

export const sortOrdersForDealerPdf = (sessions = [], orders = []) => {
  const sessionRank = new Map();
  sortDealerSessionsByDate(sessions).forEach((session, index) => {
    sessionRank.set(session.id, index);
  });

  return [...orders].sort((a, b) => {
    const rankA = sessionRank.get(a.sessionId) ?? 9999;
    const rankB = sessionRank.get(b.sessionId) ?? 9999;
    if (rankA !== rankB) return rankA - rankB;

    const createdA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
    const createdB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
    return createdB - createdA;
  });
};

export const buildSessionDateLookup = (sessions = []) => {
  const map = {};
  sessions.forEach((session) => {
    map[session.id] = formatDealerSessionDateShort(session);
  });
  return map;
};

export const computeDealerAggregateSummary = (sessions = [], orders = [], directPayments = []) => {
  const balances = aggregateDealerSessionBalances(sessions, directPayments);
  const orderLineTotal = orders.reduce((sum, order) => {
    const price = (order.discountedPrice && parseFloat(order.discount || 0) > 0)
      ? parseFloat(order.discountedPrice || 0)
      : parseFloat(order.totalPrice || 0);
    return sum + price;
  }, 0);

  return {
    sessionCount: sessions.length,
    orderCount: orders.length,
    grandTotal: balances.grandTotal,
    sessionPaid: balances.sessionPaid,
    directPaid: balances.directPaid,
    grandPaid: balances.grandPaid,
    netBalance: balances.netBalance,
    openBalance: balances.openBalance,
    creditBalance: balances.creditBalance,
    isCreditor: balances.isCreditor,
    isBalanced: balances.isBalanced,
    orderLineTotal,
    sessionTotals: sessions.map((session) => ({
      id: session.id,
      dateLabel: formatDealerSessionDateLabel(session),
      total: getDealerSessionEffectiveTotal(session),
      paid: parseFloat(session.paidAmount || 0) || 0,
      remaining: getDealerSessionEffectiveTotal(session) - (parseFloat(session.paidAmount || 0) || 0)
    }))
  };
};

export const formatDealerPdfDateRangeLabel = (fromDate, toDate, mode = 'range') => {
  if (mode === 'all') return 'Tum Zamanlar';
  const fmt = (value) => {
    if (!value) return '—';
    return new Date(`${value}T00:00:00`).toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };
  return `${fmt(fromDate)} - ${fmt(toDate)}`;
};
